import type { FC } from 'react';
import React from 'react';

import { Box, Inline, xcss } from '@atlaskit/primitives';
import { R400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Form, { Field } from '@atlaskit/form';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import InlineDialog from '@atlaskit/inline-dialog';

import { EditorTitleTextArea } from './editorTitleStyled';
import { EditorTitleContainer } from './shared-components/EditorTitleContainer';
import { SSRTitleScript } from './SSRTitleScript';

const titleStyles = xcss({
	width: '100%',
});

const errorIconStyles = xcss({
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '100%',
	margin: 'space.050',
	marginBottom: 'space.0',
});

const fieldStyles = xcss({
	// This removes the default top margin that comes with the atlas Field component.
	marginTop: 'space.negative.100',
});

export type LivePagesTitlePresentationProps = {
	title: string;
	isDisabled?: boolean;
	viewMode?: boolean;
	isTitleCenterAligned?: boolean;
	isUserEditing: boolean;
	updateError: any;
	updateLoading: boolean;
	handleBlur: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
	handleFocus: () => void;
	handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>, fieldProps: any) => void;
	handleKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	adjustTextAreaHeight: (textAreaRef: React.RefObject<HTMLTextAreaElement>) => void;
	getIsInvalidTitle: (title: string | undefined) => boolean;
	buildErrorMessage: (fieldProps: any, error: any) => string;
	textAreaRef: React.RefObject<HTMLTextAreaElement>;
	formatMessage: (obj: any) => string;
	i18n: any;
	AI_SUGGEST_TITLE_PROPS: any;
	maxLength: number;
};

export const LivePagesTitlePresentation: FC<LivePagesTitlePresentationProps> = ({
	title,
	isDisabled,
	viewMode,
	isTitleCenterAligned,
	isUserEditing,
	updateError,
	updateLoading,
	handleBlur,
	handleFocus,
	handleChange,
	handleKeyDown,
	getIsInvalidTitle,
	buildErrorMessage,
	textAreaRef,
	formatMessage,
	i18n,
	AI_SUGGEST_TITLE_PROPS,
	maxLength,
}) => (
	<Box xcss={titleStyles}>
		<Form onSubmit={() => {}}>
			{({ formProps }) => (
				<form {...formProps}>
					<Box xcss={fieldStyles}>
						<Field name="livepages-title" defaultValue={title}>
							{({ fieldProps, error }: any) => (
								<InlineDialog
									isOpen={
										!isDisabled &&
										!!(
											fieldProps.isInvalid ||
											(!updateLoading && updateError && !isUserEditing) ||
											getIsInvalidTitle(fieldProps?.value)
										)
									}
									content={
										<Inline space="space.025" alignBlock="start">
											<Box xcss={errorIconStyles}>
												<ErrorIcon
													label="error"
													spacing="none"
													color={token('color.icon.danger', R400)}
													LEGACY_margin="-4px"
												/>
											</Box>
											{buildErrorMessage(fieldProps, error)}
										</Inline>
									}
									placement={isTitleCenterAligned ? 'bottom' : 'bottom-start'}
									strategy="absolute"
								>
									<EditorTitleContainer>
										<EditorTitleTextArea
											{...fieldProps}
											{...AI_SUGGEST_TITLE_PROPS}
											data-testid="editor-title-text-area"
											minimumRows={1}
											rows={1}
											isTitleCenterAligned={isTitleCenterAligned}
											isInvalid={error}
											disabled={isDisabled}
											readOnly={viewMode}
											aria-readonly={viewMode}
											aria-disabled={isDisabled}
											ref={textAreaRef}
											maxLength={maxLength}
											aria-label={formatMessage(i18n.placeholder)}
											placeholder={formatMessage(i18n.placeholder)}
											shouldAnimate={false}
											autoComplete="off"
											resize="vertical"
											onChange={(e) => handleChange(e, fieldProps)}
											onFocus={handleFocus}
											onBlur={handleBlur}
											onKeyDown={handleKeyDown}
										/>
										<SSRTitleScript selector="textarea[name=livepages-title]" />
									</EditorTitleContainer>
								</InlineDialog>
							)}
						</Field>
					</Box>
				</form>
			)}
		</Form>
	</Box>
);
